import { watch, type WatchStopHandle } from "vue";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";

function activeStringEquals(m: Maybe<string>, value: string): boolean {
  return isJust(m) && m.value === value;
}
import type { Router } from "vue-router";
import { listDeletedFiles } from "../lib/tauri/git";
import { clearActiveEditorBuffer, setLastRoute } from "../lib/tauri/project";
import {
  databaseEntryFileName,
  databaseMapFileName,
  mapPathFromRouteParam,
  noteNameFromRouteParam,
  relativeFromManuscriptParams,
  sectionFromPath,
  trashNameFromRouteParam,
} from "../router/projectPaths";
import { useEditorSaveStore } from "../stores/useEditorSaveStore";
import { useEditorNavStore } from "../stores/useEditorNavStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";

/**
 * After navigation settles, load the document implied by the route and update
 * sticky nav + (desktop-only) `.zwlocal` lastRoute. Does not block navigation.
 */
export function installRouteDocumentSync(router: Router): WatchStopHandle {
  const session = useEditorSessionStore();
  const workspace = useProjectWorkspaceStore();
  const nav = useEditorNavStore();

  const save = useEditorSaveStore();
  const stopSaveGuard = router.beforeEach(async (to, from) => {
    if (to.path === from.path || session.isProject === false) return;
    const ok = await save.flushSave();
    if (ok === true) return;
    window.alert(
      "Your changes could not be saved. This document will stay open so you can retry saving.",
    );
    return false;
  });

  let syncGeneration = 0;

  async function blankActiveFile(): Promise<void> {
    // Section landing pages clear the buffer without selecting another file.
    // Save the outgoing document before losing its identity.
    await useEditorSaveStore().flushSave();
    session.suppressDirty = true;
    session.activePath = nothing;
    session.doc = "";
    session.synopsis = "";
    try {
      await clearActiveEditorBuffer();
    } catch (error) {
      console.error("Failed to clear active editor buffer", error);
    }
  }

  async function syncFromRoute(): Promise<void> {
    const route = router.currentRoute.value;
    if (session.isProject === false || isNothing(session.rootPath)) return;
    const root = session.rootPath.value;
    if (route.matched.some((r) => r.name === "boot")) return;

    const fullPath = route.fullPath;
    nav.rememberPath(fullPath);

    const pathWithoutQuery = fullPath.split("?")[0] ?? "/";
    const section = sectionFromPath(
      pathWithoutQuery.length > 0 ? pathWithoutQuery : "/",
    );
    if (isJust(section)) {
      const sectionId = section.value;
      session.activeTab =
        sectionId === "manuscript"
          ? just("manuscript")
          : sectionId === "storyboard"
            ? just("storyboard")
            : sectionId === "notes"
              ? just("notes")
              : sectionId === "database"
                ? just("database")
                : sectionId === "trash"
                  ? just("trash")
                  : session.activeTab;
    }

    // Trash preview is section-local. Leaving Trash must drop it so the
    // title bar / read-only buffer cannot leak onto manuscript/notes/etc.
    if (
      isJust(section) &&
      section.value !== "trash" &&
      isJust(session.trashPreview)
    ) {
      workspace.onClearTrashPreview();
    }

    // Desktop-only persistence — no-op online.
    void setLastRoute(
      root,
      pathWithoutQuery.length > 0 ? pathWithoutQuery : "/",
    );

    const gen = ++syncGeneration;

    try {
      switch (route.name) {
        case "manuscript": {
          await blankActiveFile();
          break;
        }
        case "manuscript-scene": {
          const chapter = String(route.params.chapter ?? "");
          const scene = String(route.params.scene ?? "");
          if (chapter.length === 0 || scene.length === 0) break;
          const relative = relativeFromManuscriptParams(chapter, scene);
          if (
            activeStringEquals(session.activeBasename, relative) &&
            session.editingSidecar === false
          ) {
            break;
          }
          await workspace.onSelectProjectFile(relative, { skipNavigate: true });
          break;
        }
        case "manuscript-map": {
          const mapPath = mapPathFromRouteParam(
            String(route.params.path ?? ""),
          );
          if (mapPath.length === 0) break;
          if (activeStringEquals(session.activeBasename, mapPath)) {
            break;
          }
          await workspace.onSelectManuscriptMap(mapPath, {
            skipNavigate: true,
          });
          break;
        }
        case "notes": {
          await blankActiveFile();
          break;
        }
        case "notes-item": {
          const name = noteNameFromRouteParam(String(route.params.name ?? ""));
          if (name.length === 0) break;
          if (name.includes("/")) {
            if (activeStringEquals(session.activeNote, name) === false) {
              await workspace.onSelectParkedItem(name, { skipNavigate: true });
            }
          } else if (activeStringEquals(session.activeNote, name) === false) {
            await workspace.onSelectProjectNote(name, { skipNavigate: true });
          }
          break;
        }
        case "database": {
          session.databaseGraphView = false;
          await blankActiveFile();
          break;
        }
        case "database-graph": {
          session.databaseGraphView = true;
          await blankActiveFile();
          break;
        }
        case "database-entry": {
          session.databaseGraphView = false;
          const name = databaseEntryFileName(String(route.params.name ?? ""));
          if (name.length === 0) break;
          if (activeStringEquals(session.activeDatabase, name) === false) {
            await workspace.onSelectProjectDatabase(name, {
              skipNavigate: true,
            });
          }
          break;
        }
        case "database-map": {
          session.databaseGraphView = false;
          const name = databaseMapFileName(String(route.params.name ?? ""));
          if (name.length === 0) break;
          if (activeStringEquals(session.activeDatabase, name) === false) {
            await workspace.onSelectProjectDatabase(name, {
              skipNavigate: true,
            });
          }
          break;
        }
        case "trash": {
          await blankActiveFile();
          workspace.onClearTrashPreview();
          break;
        }
        case "trash-item": {
          // Drop the live file so SceneTitleBar / entity chrome cannot keep
          // showing whatever was open before Trash. Then load the deleted
          // blob the same way notes/scenes load from the route.
          const name = trashNameFromRouteParam(String(route.params.name ?? ""));
          await blankActiveFile();
          if (name.length === 0) {
            workspace.onClearTrashPreview();
            break;
          }
          if (
            isJust(session.trashPreview) &&
            session.trashPreview.value.path === name
          ) {
            break;
          }
          const items = await listDeletedFiles(root);
          if (gen !== syncGeneration) return;
          const item = toMaybe(
            items.find((entry) => entry.relativePath === name),
          );
          if (isNothing(item)) {
            workspace.onClearTrashPreview();
            break;
          }
          await workspace.onPreviewDeleted(
            item.value.relativePath,
            item.value.oid,
          );
          break;
        }
        case "storyboard":
          break;
        default:
          break;
      }
    } finally {
      if (gen !== syncGeneration) {
        /* superseded */
      }
    }
  }

  const stopWatch = watch(
    () => router.currentRoute.value.fullPath,
    () => {
      void syncFromRoute();
    },
    { flush: "post" },
  );
  return () => {
    stopSaveGuard();
    stopWatch();
  };
}
